import { AnimatePresence, motion } from "framer-motion";
import { X, Minus, Plus, Trash2, Copy, CheckCircle2 } from "lucide-react";
import { useState } from "react";
import { useCart } from "../context/CartContext";
import api from "../lib/api";
import { toast } from "sonner";

const PAYID = "prosperouspoly@gmail.com";
const SHIPPING = 15; // AUD flat-rate express postage, Australia-wide

export default function CartDrawer() {
  const { items, isOpen, setOpen, updateQty, removeItem, total, clear } = useCart();
  const grandTotal = total + SHIPPING;
  const [stage, setStage] = useState("cart"); // cart | form | done
  const [form, setForm] = useState({ customer_name: "", email: "", phone: "", note: "" });
  const [submitting, setSubmitting] = useState(false);
  const [orderRef, setOrderRef] = useState("");

  const close = () => { setOpen(false); setTimeout(() => setStage("cart"), 300); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_name || !form.email) return;
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        items: items.map((i) => ({ product_id: i.id, name: i.name, price: i.price, qty: i.qty })),
        total: grandTotal,
      };
      const { data } = await api.post("/orders", payload);
      setOrderRef(data.id.slice(0, 8).toUpperCase());
      setStage("done");
      clear();
    } catch {
      toast.error("Could not place your inquiry. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const copyPayId = () => { navigator.clipboard.writeText(PAYID); toast.success("PayID copied"); };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={close} className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[300]" data-testid="cart-overlay"
          />
          <motion.aside
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="fixed top-0 right-0 h-full w-full sm:w-[440px] bg-[#0A0A0C] border-l border-white/10 z-[301] flex flex-col"
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between px-6 h-16 border-b border-white/10">
              <h3 className="font-display font-black uppercase tracking-tight text-lg">
                {stage === "done" ? "Confirmed" : stage === "form" ? "Checkout" : "Your Cart"}
              </h3>
              <button onClick={close} className="text-slate-400 hover:text-[#00F0FF]" data-testid="cart-close-btn"><X size={20} /></button>
            </div>

            <div className="flex-1 overflow-y-auto">
              {stage === "cart" && (
                <div className="p-6">
                  {items.length === 0 ? (
                    <p className="text-slate-500 font-mono2 text-sm text-center mt-16" data-testid="cart-empty">Your cart is empty.</p>
                  ) : items.map((i) => (
                    <div key={i.id} className="flex gap-4 py-4 border-b border-white/5" data-testid={`cart-item-${i.id}`}>
                      <img src={i.image} alt={i.name} className="w-16 h-20 object-cover bg-black rounded vial-glow" />
                      <div className="flex-1">
                        <p className="font-mono2 text-xs uppercase tracking-wide leading-tight">{i.name}</p>
                        <p className="text-[#00F0FF] font-display font-bold mt-1">${(i.price * i.qty).toFixed(2)}</p>
                        <div className="flex items-center gap-3 mt-2">
                          <button onClick={() => updateQty(i.id, i.qty - 1)} className="border border-white/20 p-1 hover:border-[#00F0FF]" data-testid={`cart-dec-${i.id}`}><Minus size={12} /></button>
                          <span className="text-sm w-5 text-center">{i.qty}</span>
                          <button onClick={() => updateQty(i.id, i.qty + 1)} className="border border-white/20 p-1 hover:border-[#00F0FF]" data-testid={`cart-inc-${i.id}`}><Plus size={12} /></button>
                          <button onClick={() => removeItem(i.id)} className="ml-auto text-slate-500 hover:text-[#FF003C]" data-testid={`cart-remove-${i.id}`}><Trash2 size={14} /></button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {stage === "form" && (
                <form onSubmit={submit} className="p-6 space-y-4" data-testid="checkout-form">
                  {[
                    { k: "customer_name", label: "Full Name", type: "text", req: true },
                    { k: "email", label: "Email", type: "email", req: true },
                    { k: "phone", label: "Phone (optional)", type: "text", req: false },
                  ].map((f) => (
                    <div key={f.k}>
                      <label className="text-[10px] uppercase tracking-widest text-slate-400 font-mono2">{f.label}</label>
                      <input
                        type={f.type} required={f.req} value={form[f.k]}
                        onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                        className="mt-1 w-full bg-black border border-white/15 focus:border-[#00F0FF] outline-none px-3 py-2.5 text-sm font-mono2"
                        data-testid={`checkout-${f.k}`}
                      />
                    </div>
                  ))}
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-slate-400 font-mono2">Notes (optional)</label>
                    <textarea
                      rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
                      className="mt-1 w-full bg-black border border-white/15 focus:border-[#00F0FF] outline-none px-3 py-2.5 text-sm font-mono2"
                      data-testid="checkout-note"
                    />
                  </div>
                </form>
              )}

              {stage === "done" && (
                <div className="p-6 text-center mt-6" data-testid="checkout-done">
                  <CheckCircle2 size={48} className="text-[#00F0FF] mx-auto mb-4" />
                  <p className="font-display font-bold text-lg">Inquiry Received</p>
                  <p className="text-slate-400 font-mono2 text-xs mt-1">Ref #{orderRef}</p>
                  <div className="mt-6 border border-[#00F0FF]/30 bg-[#00F0FF]/[0.04] p-5 text-left">
                    <p className="text-[10px] uppercase tracking-widest text-[#00F0FF] font-mono2 mb-3">Pay via PayID to confirm</p>
                    <div className="flex items-center justify-between bg-black border border-white/10 px-3 py-2.5">
                      <span className="font-mono2 text-sm" data-testid="payid-value">{PAYID}</span>
                      <button onClick={copyPayId} className="text-[#00F0FF] hover:opacity-70" data-testid="copy-payid-btn"><Copy size={15} /></button>
                    </div>
                    <p className="text-slate-500 font-mono2 text-[11px] mt-3 leading-relaxed">
                      Send the exact total shown, then reply to our confirmation email with your Ref #. We'll dispatch once payment clears.
                    </p>
                  </div>
                  <button onClick={close} className="mt-6 w-full bg-[#00F0FF] text-black py-3 text-xs uppercase tracking-widest font-bold font-mono2" data-testid="checkout-close-done">Done</button>
                </div>
              )}
            </div>

            {stage !== "done" && items.length > 0 && (
              <div className="border-t border-white/10 p-6">
                <div className="space-y-2 mb-4 font-mono2 text-sm">
                  <div className="flex justify-between text-slate-400">
                    <span>Subtotal</span>
                    <span className="text-slate-200" data-testid="cart-subtotal">${total.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-slate-400">
                    <span>Express Postage <span className="text-slate-600">(AUD, Australia-wide)</span></span>
                    <span className="text-slate-200" data-testid="cart-shipping">${SHIPPING.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center pt-2 border-t border-white/5">
                    <span className="text-slate-400 uppercase tracking-widest text-xs">Total</span>
                    <span className="font-display font-black text-2xl text-[#00F0FF]" data-testid="cart-total">${grandTotal.toFixed(2)}</span>
                  </div>
                </div>
                {stage === "cart" ? (
                  <button onClick={() => setStage("form")} className="w-full bg-[#00F0FF] text-black py-3.5 text-xs uppercase tracking-widest font-bold font-mono2 hover:shadow-[0_0_25px_rgba(0,240,255,0.5)] transition-shadow" data-testid="cart-checkout-btn">
                    Checkout
                  </button>
                ) : (
                  <button onClick={submit} disabled={submitting} className="w-full bg-[#00F0FF] text-black py-3.5 text-xs uppercase tracking-widest font-bold font-mono2 disabled:opacity-50" data-testid="checkout-submit-btn">
                    {submitting ? "Submitting…" : "Place Inquiry"}
                  </button>
                )}
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
